import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@stores/store';
import { Button, Menu, MenuItem, ListItemIcon } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useAddUserPreferenceMutation } from '@api/userDetails';
import { LANGUAGES } from '@constants/constants';
import i18n from '@utils/i18next';

interface LanguageSelectorProps {
  anchorEl: null | HTMLElement;
  setAnchorEl: React.Dispatch<React.SetStateAction<null | HTMLElement>>;
}

const languageCodeMap: Record<string, string> = {
  EN: 'en',
  ZH: 'zh-CN',
};

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ anchorEl, setAnchorEl }) => {
  const open = Boolean(anchorEl);

  const userData = useSelector((state: RootState) => state.UserData);
  const [setPreference] = useAddUserPreferenceMutation();

  useEffect(() => {
    const updateUserPreferences = async () => {
      try {
        const userLocale = languageCodeMap[userData?.user?.preference?.language || 'EN'];
        const urlLocale = new URLSearchParams(window.location.search).get('locale');

        if (urlLocale && LANGUAGES.map((l) => l.locale).includes(urlLocale)) {
          const languageCode =
            LANGUAGES.find((language) => language.locale === urlLocale)?.code || 'EN';
          await setPreference({ language: languageCode });
          i18n.changeLanguage(urlLocale);
          localStorage.setItem('app_locale', urlLocale);
        } else if (userLocale) {
          i18n.changeLanguage(userLocale);
          localStorage.setItem('app_locale', userLocale);
        } else {
          i18n.changeLanguage('en');
          localStorage.setItem('app_locale', 'en');
        }
      } catch (e) {
        console.error(e);
      }
    };

    if (userData?.user?.id) {
      updateUserPreferences();
    }
  }, [userData?.user?.id]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = async (selectedLocale: string) => {
    if (selectedLocale !== i18n.language) {
      i18n.changeLanguage(selectedLocale);
      localStorage.setItem('app_locale', selectedLocale);
      const languageCode =
        LANGUAGES.find((language) => language.locale === selectedLocale)?.code || 'EN';
      try {
        await setPreference({ language: languageCode });
      } catch (e) {
        console.error(e);
      }
    }

    // Remove locale search param from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('locale')) {
      urlParams.delete('locale');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    }

    handleClose();
  };

  const currentLang = LANGUAGES.find((l) => l.locale === i18n.language);

  return (
    <>
      <Button
        disableTouchRipple
        disableElevation
        disableRipple
        disableFocusRipple
        endIcon={<NavigateNextIcon sx={{ color: 'white' }} />}
        onClick={handleClick}
        sx={{
          color: '#51B8B0',
          fontWeight: 700,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          fontFamily: 'Manrope,sans-serif',
        }}
      >
        {currentLang?.displayLabel}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        MenuListProps={{
          sx: {
            width: '200px',
            padding: 0,
          },
        }}
      >
        {LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.locale}
            onClick={() => handleSelect(lang.locale)}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              padding: '1rem 0.75rem ',
              fontWeight: 500,
              fontSize: '1rem',
              lineHeight: '1.375rem',
              fontFamily: 'Manrope,sans-serif',
              color: '#162936',
            }}
          >
            {lang.label}
            {i18n.language === lang.locale && (
              <ListItemIcon>
                <CheckIcon fontSize="small" sx={{ color: '#162C36' }} />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;
